import { useState } from "react";
import type { CaseRecord, RecordFact } from "../../types";
import { displayFactTitle, factMeta, factSourceLabel } from "../../lib/factMeta";
import type { PartyUi } from "../../lib/parties";
import FactProvenance from "./FactProvenance";
import { AiMark, VerifiedMark } from "./ProvenanceMark";

// One verified fact: a type-coded badge, a short summary, a collapsible
// verbatim quote (the exact source substring), and a source-record link.
// Every fact shown here is already substring-verified against its source —
// no validation badge is needed.
export default function FactCard({
  fact,
  record,
  onNavigateToRecord,
  onViewQuote,
  defaultExpanded = false,
  hideSource = false,
  alsoIn = [],
  recordById,
  partyUi,
}: {
  fact: RecordFact;
  record?: CaseRecord;
  onNavigateToRecord?: (recordId: string) => void;
  // Opens the quote-in-context view (source record with the quote
  // highlighted). Optional — callers without the panel still render.
  onViewQuote?: (fact: RecordFact) => void;
  defaultExpanded?: boolean;
  // When rendered inside a per-record group the source line is redundant
  // (the group header already links the record).
  hideSource?: boolean;
  // Other records carrying this same (deduplicated) fact, plus the lookup
  // needed to label them. Omitted for non-deduplicated callers.
  alsoIn?: string[];
  recordById?: Map<string, CaseRecord>;
  // Canonical party display + tap-to-open-panel (Round 10). Placeholders
  // ("Unknown impound lot staff") render but are never tappable.
  partyUi?: PartyUi;
}) {
  const [open, setOpen] = useState(defaultExpanded);
  const meta = factMeta(fact.fact_type);
  const lowTrust = fact.trust_level === "low";
  // "mentioned in <title> (<record date>)" when the fact's event date
  // differs from the source record's date; plain title otherwise.
  const source = factSourceLabel(fact, record);
  const sourceLabel = source.text;

  return (
    <div className="fact-card">
      <div className="fact-card-head">
        <span
          className="fact-badge"
          style={{ backgroundColor: meta.color }}
          title={meta.label}
        >
          {meta.emoji} {meta.label}
        </span>
        <span className="fact-summary">
          {displayFactTitle(fact)} <AiMark what="Fact title" />
        </span>
        {fact.amount && (
          <span className="fact-amount">
            {lowTrust ? "≈ " : ""}
            {fact.amount} <VerifiedMark what="Amount" />
          </span>
        )}
      </div>

      {fact.party && (
        <div className="fact-party">
          {partyUi?.clickable(fact.party) ? (
            <button
              type="button"
              className="fact-party-link"
              onClick={() => partyUi.open(fact.party!)}
              title="View party details"
            >
              {partyUi.display(fact.party)}
            </button>
          ) : (
            <span>{partyUi?.display(fact.party) ?? fact.party}</span>
          )}{" "}
          <AiMark what="Party attribution" />
        </div>
      )}

      {/* Link, don't dump (Round 13, Phase 1): when a focused quote panel is
          available, the quote opens there instead of unfurling inline and
          pushing the page down. Callers without the panel (exports, print,
          the layout fixture) keep the inline toggle as a fallback. */}
      {onViewQuote ? (
        <button
          type="button"
          className="fact-quote-toggle"
          onClick={() => onViewQuote(fact)}
        >
          ▸ View verbatim quote in context
        </button>
      ) : (
        <>
          <button
            type="button"
            className="fact-quote-toggle"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            {open ? "▾ Hide quote" : "▸ Show verbatim quote"}
          </button>
          {open && (
            <blockquote className={`fact-quote${lowTrust ? " low-trust" : ""}`}>
              <VerifiedMark what="Verbatim quote" /> “{fact.verbatim_quote}”
            </blockquote>
          )}
        </>
      )}

      <div className="fact-source">
        {!hideSource && (
          <>
            {source.dateContext ? "" : "Source: "}
            {onNavigateToRecord ? (
              <button
                type="button"
                className="fact-source-link"
                onClick={() => onNavigateToRecord(fact.record_id)}
              >
                {sourceLabel}
              </button>
            ) : (
              <span>{sourceLabel}</span>
            )}
          </>
        )}
        {lowTrust && (
          <span className="fact-trust-flag" title="Extracted from an audio transcription">
            {" "}
            ⚠ Transcription
          </span>
        )}
      </div>

      {recordById && (
        <FactProvenance
          recordIds={[fact.record_id, ...alsoIn]}
          recordById={recordById}
          onNavigateToRecord={onNavigateToRecord}
        />
      )}
    </div>
  );
}
